import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { Tag, Printer, Minus, Plus, Grid3x3, Square, Image as ImageIcon, FileCode } from "lucide-react";
import { toast } from "sonner";

const ALLERGEN_OPTIONS = ["Wheat / Gluten", "Milk / Dairy", "Eggs", "Peanuts", "Tree nuts", "Soy", "Sesame", "Fish", "Shellfish"];

// Individual label sizes for "Single label" mode.
const LABEL_SIZES = [
  { key: "2x4",    label: "2\" × 4\" (rectangle)", widthIn: 4,   heightIn: 2,   shape: "rect",   defaults: [14, 8,  7,  7] },
  { key: "2x2",    label: "2\" × 2\" (square)",    widthIn: 2,   heightIn: 2,   shape: "rect",   defaults: [11, 7,  6,  6] },
  { key: "3x3",    label: "3\" × 3\" (square)",    widthIn: 3,   heightIn: 3,   shape: "rect",   defaults: [14, 9,  7,  7] },
  { key: "4x6",    label: "4\" × 6\" (rectangle)", widthIn: 4,   heightIn: 6,   shape: "rect",   defaults: [20, 12, 10, 10] },
  { key: "round25", label: "2.5\" round",          widthIn: 2.5, heightIn: 2.5, shape: "circle", defaults: [12, 7,  6,  6] },
];

// Avery sheet templates. All on US Letter 8.5" × 11".
// Values from Avery datasheets; small approximations are safe because printers
// usually offer a nudge/scale step on their own paper feed.
const AVERY_TEMPLATES = [
  { key: "5160", name: "Avery 5160 (30 per sheet)",  cols: 3, rows: 10, labelW: 2.625, labelH: 1,    marginTop: 0.5,   marginLeft: 0.1875,   gutterX: 0.125,   gutterY: 0,    shape: "rect",   defaults: [8,  6,  5,  5] },
  { key: "5163", name: "Avery 5163 (10 per sheet)",  cols: 2, rows: 5,  labelW: 4,     labelH: 2,    marginTop: 0.5,   marginLeft: 0.15625,  gutterX: 0.1875,  gutterY: 0,    shape: "rect",   defaults: [14, 8,  7,  7] },
  { key: "5164", name: "Avery 5164 (6 per sheet)",   cols: 2, rows: 3,  labelW: 4,     labelH: 3.33, marginTop: 0.5,   marginLeft: 0.15625,  gutterX: 0.15625, gutterY: 0,    shape: "rect",   defaults: [16, 10, 8,  8] },
  { key: "22807", name: "Avery 22807 (12 round)",    cols: 3, rows: 4,  labelW: 2,     labelH: 2,    marginTop: 0.75,  marginLeft: 0.75,     gutterX: 0.375,   gutterY: 0.375, shape: "circle", defaults: [10, 6,  5,  5] },
];

const PREVIEW_MAX_W = 340;
const PREVIEW_MAX_H = 520;
const PX_PER_IN = 96;
const SHEET_W_IN = 8.5;
const SHEET_H_IN = 11;

export default function LabelDesigner() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [productName, setProductName] = useState("Chocolate Sourdough");
  const [businessName, setBusinessName] = useState(user?.username ? `${user.username}'s Bakery` : "Home Bakery");
  const [businessAddress, setBusinessAddress] = useState("Made in a home kitchen. Prepared under a cottage food license.");
  const [netWeight, setNetWeight] = useState("500g");
  const [ingredients, setIngredients] = useState("Unbleached wheat flour, water, dark chocolate (cocoa, sugar, cocoa butter), starter, salt.");
  const [allergens, setAllergens] = useState(["Wheat / Gluten"]);
  const [nutrition, setNutrition] = useState({ servings: 8, servingSize: "60g", calories: 180, fat: 3, carbs: 34, sugar: 6, protein: 5, sodium: 320 });
  const [bestBy, setBestBy] = useState("");
  const [showNutrition, setShowNutrition] = useState(true);
  const [sizeKey, setSizeKey] = useState("2x4");
  const [layoutMode, setLayoutMode] = useState("single");   // "single" | "avery"
  const [averyKey, setAveryKey] = useState("5160");
  const [copies, setCopies] = useState(30);
  const [fsProduct, setFsProduct] = useState(14);
  const [fsBusiness, setFsBusiness] = useState(8);
  const [fsIngredients, setFsIngredients] = useState(7);
  const [fsNutrition, setFsNutrition] = useState(7);

  const singleSize = LABEL_SIZES.find((s) => s.key === sizeKey) || LABEL_SIZES[0];
  const avery = AVERY_TEMPLATES.find((a) => a.key === averyKey) || AVERY_TEMPLATES[0];
  const totalAveryLabels = avery.cols * avery.rows;

  // Active label geometry (used by the printable label element)
  const size = layoutMode === "avery"
    ? { widthIn: avery.labelW, heightIn: avery.labelH, shape: avery.shape, defaults: avery.defaults }
    : singleSize;

  // Reset font sizes when the active geometry changes
  useEffect(() => {
    const [p, b, i, n] = size.defaults;
    setFsProduct(p); setFsBusiness(b); setFsIngredients(i); setFsNutrition(n);
    // eslint-disable-next-line
  }, [sizeKey, layoutMode, averyKey]);

  // Clamp copies to sheet capacity when Avery template changes
  useEffect(() => {
    setCopies((c) => Math.min(totalAveryLabels, Math.max(1, c || totalAveryLabels)));
    // eslint-disable-next-line
  }, [averyKey]);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) nav("/login");
  }, [user, loading, nav]);

  const toggleAllergen = (a) => setAllergens((prev) => prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]);

  // Compute a preview scale so the label fits within the on-screen preview column.
  const naturalW = size.widthIn * PX_PER_IN;
  const naturalH = size.heightIn * PX_PER_IN;
  const scale = useMemo(() => Math.min(1, PREVIEW_MAX_W / naturalW, PREVIEW_MAX_H / naturalH), [naturalW, naturalH]);
  const scaledW = naturalW * scale;
  const scaledH = naturalH * scale;

  // pt → px conversion (1pt = 1/72 in)
  const ptPx = (pt) => `${(pt / 72) * PX_PER_IN}px`;

  // Ref to the exact-size unscaled label element for exports
  const exportLabelRef = useRef(null);

  const slug = () => (productName || "label").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "label";
  const outWidthIn = layoutMode === "avery" ? SHEET_W_IN : size.widthIn;
  const outHeightIn = layoutMode === "avery" ? SHEET_H_IN : size.heightIn;

  const exportPNG = async () => {
    if (!exportLabelRef.current) return;
    try {
      const { default: html2canvas } = await import("html2canvas-pro");
      // Render at 3x pixel density for crisp Canva imports
      const scale3x = 3;
      const canvas = await html2canvas(exportLabelRef.current, {
        backgroundColor: "#ffffff",
        scale: scale3x,
        useCORS: true,
        logging: false,
      });
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug()}-${layoutMode === "avery" ? avery.key + "-sheet" : size.widthIn + "x" + size.heightIn}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("PNG downloaded");
    } catch (e) {
      toast.error(`PNG export failed: ${e?.message || e}`);
    }
  };

  const exportSVG = () => {
    if (!exportLabelRef.current) return;
    try {
      // Wrap a clone of the label in an SVG foreignObject so all HTML+CSS
      // renders identically inside vector tools like Canva/Figma.
      const wPx = outWidthIn * PX_PER_IN;
      const hPx = outHeightIn * PX_PER_IN;
      const clone = exportLabelRef.current.cloneNode(true);
      // Force explicit pixel width/height on the clone so the foreignObject fills the SVG viewport
      clone.style.width = `${wPx}px`;
      clone.style.height = `${hPx}px`;
      clone.style.transform = "none";

      // Inline computed styles onto every descendant (Tailwind classes won't survive outside the app).
      const inlineStyles = (src, dst) => {
        const cs = window.getComputedStyle(src);
        const declared = [
          "color", "background", "background-color", "font-family", "font-size", "font-weight", "font-style",
          "line-height", "letter-spacing", "text-align", "text-transform", "text-decoration",
          "padding", "padding-top", "padding-right", "padding-bottom", "padding-left",
          "margin", "margin-top", "margin-right", "margin-bottom", "margin-left",
          "border", "border-top", "border-right", "border-bottom", "border-left",
          "border-radius", "border-color", "border-style", "border-width",
          "width", "height", "display", "flex-direction", "align-items", "justify-content",
          "overflow", "box-sizing", "position", "left", "top", "right", "bottom",
        ];
        let out = "";
        for (const k of declared) out += `${k}:${cs.getPropertyValue(k)};`;
        dst.setAttribute("style", out);
      };
      const srcNodes = exportLabelRef.current.querySelectorAll("*");
      const dstNodes = clone.querySelectorAll("*");
      inlineStyles(exportLabelRef.current, clone);
      srcNodes.forEach((s, i) => { if (dstNodes[i]) inlineStyles(s, dstNodes[i]); });

      const wrapper = document.createElement("div");
      wrapper.appendChild(clone);
      const foreignHTML = wrapper.innerHTML;
      const svg = `<?xml version="1.0" standalone="no"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${outWidthIn}in" height="${outHeightIn}in" viewBox="0 0 ${wPx} ${hPx}">
  <foreignObject x="0" y="0" width="${wPx}" height="${hPx}">
    <div xmlns="http://www.w3.org/1999/xhtml">${foreignHTML}</div>
  </foreignObject>
</svg>`;
      const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug()}-${layoutMode === "avery" ? avery.key + "-sheet" : size.widthIn + "x" + size.heightIn}.svg`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("SVG downloaded");
    } catch (e) {
      toast.error(`SVG export failed: ${e?.message || e}`);
    }
  };

  // Print styles: choose between single-label mode (page = label size) and Avery-sheet mode (Letter page, absolute-positioned grid).
  const printStyles = layoutMode === "single"
    ? `@media print {
        @page { size: ${size.widthIn}in ${size.heightIn}in; margin: 0; }
        body { margin: 0 !important; background: #fff !important; }
        body * { visibility: hidden; }
        #print-label, #print-label * { visibility: visible; }
        #print-label { position: absolute !important; left: 0 !important; top: 0 !important; transform: none !important; width: ${size.widthIn}in !important; height: ${size.heightIn}in !important; }
        #print-label-frame { transform: none !important; width: auto !important; height: auto !important; }
        #print-sheet { display: none !important; }
      }`
    : `@media print {
        @page { size: ${SHEET_W_IN}in ${SHEET_H_IN}in; margin: 0; }
        body { margin: 0 !important; background: #fff !important; }
        body * { visibility: hidden; }
        #print-sheet, #print-sheet * { visibility: visible; }
        #print-sheet { position: absolute !important; left: 0 !important; top: 0 !important; width: ${SHEET_W_IN}in !important; height: ${SHEET_H_IN}in !important; transform: none !important; }
        #print-label-frame, #print-label { display: none !important; }
      }`;

  if (loading || !user) return null;

  return (
    <>
      <style>{printStyles}</style>
      <div className="print:hidden"><Header /></div>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6 print:hidden flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]"><Tag className="w-3.5 h-3.5" style={{ color: "#C2185B" }} /><span>Business tool</span></div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] mt-1">Label & <span style={{ color: "#C2185B" }}>Packaging</span> Designer</h1>
            <p className="mt-2 text-[#857163] max-w-2xl">Design compliant product labels with ingredients, allergens, and nutrition facts. Pick a size below — your Print / PDF output matches it exactly.</p>
          </div>
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white font-semibold text-sm hover:opacity-90" style={{ background: "#C2185B" }} data-testid="print-btn"><Printer className="w-4 h-4" /> Print / PDF</button>
          <button onClick={exportPNG} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white font-semibold text-sm hover:opacity-90" style={{ background: "#5E7A2E" }} data-testid="export-png-btn"><ImageIcon className="w-4 h-4" /> Export PNG</button>
          <button onClick={exportSVG} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white font-semibold text-sm hover:opacity-90" style={{ background: "#2F80ED" }} data-testid="export-svg-btn"><FileCode className="w-4 h-4" /> Export SVG</button>
        </div>

        <div className="grid lg:grid-cols-[1fr_380px] gap-6">
          {/* Form */}
          <div className="cp-card p-5 sm:p-6 print:hidden space-y-3">
            {/* Layout mode toggle */}
            <div>
              <div className="text-xs text-[#857163] mb-1.5">Layout mode</div>
              <div className="inline-flex rounded-full p-1 bg-[#FFF9EF] border border-[#EBDCD1]" data-testid="layout-mode-toggle">
                <button
                  onClick={() => setLayoutMode("single")}
                  className={`px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 transition ${layoutMode === "single" ? "bg-[#C2185B] text-white" : "text-[#3B2C24]"}`}
                  data-testid="mode-single"
                >
                  <Square className="w-3 h-3" /> Single label
                </button>
                <button
                  onClick={() => setLayoutMode("avery")}
                  className={`px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 transition ${layoutMode === "avery" ? "bg-[#C2185B] text-white" : "text-[#3B2C24]"}`}
                  data-testid="mode-avery"
                >
                  <Grid3x3 className="w-3 h-3" /> Avery sheet
                </button>
              </div>
            </div>

            {layoutMode === "single" && (
              <div>
                <div className="text-xs text-[#857163] mb-1.5">Label size</div>
                <div className="flex flex-wrap gap-1.5" data-testid="label-size-picker">
                  {LABEL_SIZES.map((s) => (
                    <button
                      key={s.key}
                      onClick={() => setSizeKey(s.key)}
                      className={`px-3 py-1.5 rounded-full text-xs border transition ${sizeKey === s.key ? "bg-[#C2185B] text-white border-[#C2185B]" : "bg-white border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"}`}
                      data-testid={`size-btn-${s.key}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {layoutMode === "avery" && (
              <div className="space-y-2" data-testid="avery-picker">
                <div className="text-xs text-[#857163]">Avery template</div>
                <select
                  className="cp-input"
                  value={averyKey}
                  onChange={(e) => setAveryKey(e.target.value)}
                  data-testid="avery-template-select"
                >
                  {AVERY_TEMPLATES.map((a) => (
                    <option key={a.key} value={a.key}>{a.name} — {a.labelW}" × {a.labelH}"{a.shape === "circle" ? " round" : ""}</option>
                  ))}
                </select>
                <div>
                  <div className="text-xs text-[#857163] mb-1">Copies to print <span className="text-[#B79885]">(max {totalAveryLabels} — fills the sheet)</span></div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setCopies(Math.max(1, copies - 1))} className="w-8 h-8 rounded-full bg-white border border-[#EBDCD1] grid place-items-center hover:bg-[#F9EFE6]" data-testid="copies-minus"><Minus className="w-3.5 h-3.5" /></button>
                    <input
                      type="number" min="1" max={totalAveryLabels}
                      className="cp-input flex-1 text-center !py-1.5"
                      value={copies}
                      onChange={(e) => setCopies(Math.min(totalAveryLabels, Math.max(1, parseInt(e.target.value, 10) || 1)))}
                      data-testid="copies-input"
                    />
                    <button onClick={() => setCopies(Math.min(totalAveryLabels, copies + 1))} className="w-8 h-8 rounded-full bg-white border border-[#EBDCD1] grid place-items-center hover:bg-[#F9EFE6]" data-testid="copies-plus"><Plus className="w-3.5 h-3.5" /></button>
                    <button
                      onClick={() => setCopies(totalAveryLabels)}
                      className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#EBDCD1] hover:bg-[#F9EFE6] text-[#3B2C24]"
                      data-testid="copies-fill"
                    >Fill sheet</button>
                  </div>
                </div>
              </div>
            )}

            <label><span className="text-xs text-[#857163]">Product name</span><input className="cp-input mt-1" value={productName} onChange={(e) => setProductName(e.target.value)} data-testid="product-name" /></label>
            <div className="grid sm:grid-cols-2 gap-3">
              <label><span className="text-xs text-[#857163]">Business name</span><input className="cp-input mt-1" value={businessName} onChange={(e) => setBusinessName(e.target.value)} data-testid="business-name" /></label>
              <label><span className="text-xs text-[#857163]">Net weight / size</span><input className="cp-input mt-1" value={netWeight} onChange={(e) => setNetWeight(e.target.value)} placeholder="500g / 1 dozen" data-testid="net-weight" /></label>
            </div>
            <label><span className="text-xs text-[#857163]">Business address / license line</span><input className="cp-input mt-1" value={businessAddress} onChange={(e) => setBusinessAddress(e.target.value)} data-testid="business-address" /></label>
            <label className="block"><span className="text-xs text-[#857163]">Ingredients (in descending order by weight)</span><textarea className="cp-input mt-1 min-h-[80px] text-sm" value={ingredients} onChange={(e) => setIngredients(e.target.value)} data-testid="ingredients" /></label>
            <div>
              <div className="text-xs text-[#857163] mb-1">Contains (allergens)</div>
              <div className="flex flex-wrap gap-1.5">
                {ALLERGEN_OPTIONS.map((a) => (
                  <button key={a} onClick={() => toggleAllergen(a)} className={`px-2.5 py-1 rounded-full text-xs border transition ${allergens.includes(a) ? "bg-[#C2185B] text-white border-[#C2185B]" : "bg-white border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"}`} data-testid={`allergen-${a.split(" ")[0].toLowerCase()}`}>{a}</button>
                ))}
              </div>
            </div>
            <label><span className="text-xs text-[#857163]">Best by / expiration</span><input className="cp-input mt-1" value={bestBy} onChange={(e) => setBestBy(e.target.value)} placeholder="Best within 3 days of purchase" data-testid="best-by" /></label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={showNutrition} onChange={(e) => setShowNutrition(e.target.checked)} className="w-4 h-4" data-testid="show-nutrition" /><span className="text-sm text-[#3B2C24]">Include nutrition facts</span></label>
            {showNutrition && (
              <div className="grid grid-cols-4 gap-2">
                <label><span className="text-[10px] text-[#857163]">Servings</span><input type="number" className="cp-input mt-1 !py-1.5 !text-sm" value={nutrition.servings} onChange={(e) => setNutrition({...nutrition, servings: e.target.value})} /></label>
                <label><span className="text-[10px] text-[#857163]">Serving size</span><input className="cp-input mt-1 !py-1.5 !text-sm" value={nutrition.servingSize} onChange={(e) => setNutrition({...nutrition, servingSize: e.target.value})} /></label>
                <label><span className="text-[10px] text-[#857163]">Calories</span><input type="number" className="cp-input mt-1 !py-1.5 !text-sm" value={nutrition.calories} onChange={(e) => setNutrition({...nutrition, calories: e.target.value})} /></label>
                <label><span className="text-[10px] text-[#857163]">Fat (g)</span><input type="number" className="cp-input mt-1 !py-1.5 !text-sm" value={nutrition.fat} onChange={(e) => setNutrition({...nutrition, fat: e.target.value})} /></label>
                <label><span className="text-[10px] text-[#857163]">Carbs (g)</span><input type="number" className="cp-input mt-1 !py-1.5 !text-sm" value={nutrition.carbs} onChange={(e) => setNutrition({...nutrition, carbs: e.target.value})} /></label>
                <label><span className="text-[10px] text-[#857163]">Sugar (g)</span><input type="number" className="cp-input mt-1 !py-1.5 !text-sm" value={nutrition.sugar} onChange={(e) => setNutrition({...nutrition, sugar: e.target.value})} /></label>
                <label><span className="text-[10px] text-[#857163]">Protein (g)</span><input type="number" className="cp-input mt-1 !py-1.5 !text-sm" value={nutrition.protein} onChange={(e) => setNutrition({...nutrition, protein: e.target.value})} /></label>
                <label><span className="text-[10px] text-[#857163]">Sodium (mg)</span><input type="number" className="cp-input mt-1 !py-1.5 !text-sm" value={nutrition.sodium} onChange={(e) => setNutrition({...nutrition, sodium: e.target.value})} /></label>
              </div>
            )}

            {/* Font size controls */}
            <div className="pt-3 border-t border-[#EBDCD1]">
              <div className="text-xs text-[#857163] mb-2">Text sizes (pt) — tune each section to fit your label</div>
              <div className="grid sm:grid-cols-2 gap-2" data-testid="font-size-controls">
                <FontSizeControl testid="fs-product"      label="Product name"    value={fsProduct}      onChange={setFsProduct} />
                <FontSizeControl testid="fs-business"     label="Business + address" value={fsBusiness}  onChange={setFsBusiness} />
                <FontSizeControl testid="fs-ingredients"  label="Ingredients"     value={fsIngredients}  onChange={setFsIngredients} />
                <FontSizeControl testid="fs-nutrition"    label="Nutrition facts" value={fsNutrition}    onChange={setFsNutrition} />
              </div>
            </div>
          </div>

          {/* Label preview — do NOT wrap in print:hidden. Only the caption + scale-notice are hidden for print. */}
          <div>
            <div className="text-xs uppercase tracking-widest text-[#857163] mb-2 text-center print:hidden" data-testid="preview-caption">
              {layoutMode === "avery"
                ? <>Preview — 1 of {copies} labels · {avery.name.split(" (")[0]}</>
                : <>Preview — {size.widthIn}″ × {size.heightIn}″ {size.shape === "circle" ? "(round)" : ""}</>}
            </div>
            <div
              id="print-label-frame"
              className="mx-auto"
              style={{ width: scaledW, height: scaledH }}
              data-testid="preview-frame"
            >
              <div
                id="print-label"
                className="bg-white border-2 border-[#3B2C24] p-2 text-[#3B2C24] overflow-hidden"
                data-testid="label-preview"
                style={{
                  width: naturalW,
                  height: naturalH,
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                  borderRadius: size.shape === "circle" ? "50%" : "8px",
                }}
              >
                <LabelContent
                  shape={size.shape}
                  productName={productName}
                  businessName={businessName}
                  businessAddress={businessAddress}
                  netWeight={netWeight}
                  ingredients={ingredients}
                  allergens={allergens}
                  nutrition={nutrition}
                  bestBy={bestBy}
                  showNutrition={showNutrition}
                  fsProduct={fsProduct}
                  fsBusiness={fsBusiness}
                  fsIngredients={fsIngredients}
                  fsNutrition={fsNutrition}
                  ptPx={ptPx}
                />
              </div>
            </div>
            <div className="text-center text-[10px] text-[#857163] mt-2 print:hidden" data-testid="preview-scale-notice">
              {layoutMode === "single"
                ? <>Displayed at {Math.round(scale * 100)}% — Print / PDF outputs at 100% ({size.widthIn}″ × {size.heightIn}″)</>
                : <>Print / PDF will output a full US Letter sheet ({SHEET_W_IN}″ × {SHEET_H_IN}″) with <b>{copies}</b> copies of your label positioned for {avery.name.split(" (")[0]}.</>}
            </div>
          </div>
        </div>

        {/* Avery sheet — hidden on-screen (opacity 0, off-flow), visible only on print */}
        {layoutMode === "avery" && (
          <div
            id="print-sheet"
            ref={exportLabelRef}
            style={{
              position: "absolute", left: "-9999px", top: 0,
              width: `${SHEET_W_IN}in`, height: `${SHEET_H_IN}in`,
              background: "#fff",
            }}
            data-testid="print-sheet"
          >
            {Array.from({ length: Math.min(copies, totalAveryLabels) }).map((_, idx) => {
              const col = idx % avery.cols;
              const row = Math.floor(idx / avery.cols);
              const left = avery.marginLeft + col * (avery.labelW + avery.gutterX);
              const top = avery.marginTop + row * (avery.labelH + avery.gutterY);
              return (
                <div
                  key={idx}
                  style={{
                    position: "absolute",
                    left: `${left}in`,
                    top: `${top}in`,
                    width: `${avery.labelW}in`,
                    height: `${avery.labelH}in`,
                    borderRadius: avery.shape === "circle" ? "50%" : 0,
                    overflow: "hidden",
                    padding: "0.06in",
                    boxSizing: "border-box",
                    color: "#3B2C24",
                    background: "#fff",
                  }}
                >
                  <LabelContent
                    shape={avery.shape}
                    productName={productName}
                    businessName={businessName}
                    businessAddress={businessAddress}
                    netWeight={netWeight}
                    ingredients={ingredients}
                    allergens={allergens}
                    nutrition={nutrition}
                    bestBy={bestBy}
                    showNutrition={showNutrition}
                    fsProduct={fsProduct}
                    fsBusiness={fsBusiness}
                    fsIngredients={fsIngredients}
                    fsNutrition={fsNutrition}
                    ptPx={ptPx}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Hidden unscaled export target for single-label mode. Placed off-screen so on-screen preview scaling doesn't affect exports. */}
        {layoutMode === "single" && (
          <div
            ref={exportLabelRef}
            style={{
              position: "absolute", left: "-9999px", top: 0,
              width: `${size.widthIn}in`, height: `${size.heightIn}in`,
              background: "#fff",
              border: "2px solid #3B2C24",
              borderRadius: size.shape === "circle" ? "50%" : "8px",
              padding: "0.06in",
              boxSizing: "border-box",
              color: "#3B2C24",
              overflow: "hidden",
            }}
            data-testid="export-target-single"
          >
            <LabelContent
              shape={size.shape}
              productName={productName}
              businessName={businessName}
              businessAddress={businessAddress}
              netWeight={netWeight}
              ingredients={ingredients}
              allergens={allergens}
              nutrition={nutrition}
              bestBy={bestBy}
              showNutrition={showNutrition}
              fsProduct={fsProduct}
              fsBusiness={fsBusiness}
              fsIngredients={fsIngredients}
              fsNutrition={fsNutrition}
              ptPx={ptPx}
            />
          </div>
        )}
      </main>
    </>
  );
}

function FontSizeControl({ label, value, onChange, testid }) {
  const clamp = (n) => Math.max(4, Math.min(72, n));
  return (
    <div className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-full bg-[#FFF9EF] border border-[#EBDCD1]" data-testid={testid}>
      <span className="text-xs text-[#3B2C24] truncate">{label}</span>
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={() => onChange(clamp(value - 1))} className="w-6 h-6 rounded-full bg-white border border-[#EBDCD1] grid place-items-center hover:bg-[#F9EFE6]" data-testid={`${testid}-minus`}><Minus className="w-3 h-3" /></button>
        <span className="text-xs font-semibold w-9 text-center" data-testid={`${testid}-value`}>{value}pt</span>
        <button onClick={() => onChange(clamp(value + 1))} className="w-6 h-6 rounded-full bg-white border border-[#EBDCD1] grid place-items-center hover:bg-[#F9EFE6]" data-testid={`${testid}-plus`}><Plus className="w-3 h-3" /></button>
      </div>
    </div>
  );
}

function LabelContent({ shape, productName, businessName, businessAddress, netWeight, ingredients, allergens, nutrition, bestBy, showNutrition, fsProduct, fsBusiness, fsIngredients, fsNutrition, ptPx }) {
  return (
    <div className={shape === "circle" ? "h-full flex flex-col items-center justify-center text-center px-4" : ""}>
      <div className="text-center mb-1">
        <div className="font-heading font-bold leading-tight" style={{ fontSize: ptPx(fsProduct) }} data-testid="label-product">{productName}</div>
        <div className="text-[#857163]" style={{ fontSize: ptPx(fsBusiness), lineHeight: 1.2 }} data-testid="label-business">{businessName}</div>
        <div className="text-[#857163] italic" style={{ fontSize: ptPx(Math.max(6, fsBusiness - 1)), lineHeight: 1.2 }} data-testid="label-address">{businessAddress}</div>
      </div>
      <div className="text-center border-t-2 border-b-2 border-[#3B2C24] py-0.5 my-1 font-bold" style={{ fontSize: ptPx(fsBusiness + 1) }} data-testid="label-net-weight">NET WT {netWeight}</div>
      {showNutrition && (
        <div className="border-2 border-[#3B2C24] p-1.5 mb-1" style={{ fontSize: ptPx(fsNutrition), lineHeight: 1.25 }} data-testid="label-nutrition">
          <div className="font-bold" style={{ fontSize: ptPx(fsNutrition + 2) }}>Nutrition Facts</div>
          <div style={{ fontSize: ptPx(Math.max(6, fsNutrition - 1)) }}>Serving size: {nutrition.servingSize} · About {nutrition.servings} servings</div>
          <div className="border-t border-[#3B2C24] mt-0.5 pt-0.5 flex justify-between font-bold"><span>Calories</span><span>{nutrition.calories}</span></div>
          <div className="border-t border-[#3B2C24] mt-0.5 pt-0.5 space-y-0.5">
            <NRow label="Total Fat" value={`${nutrition.fat}g`} />
            <NRow label="Sodium" value={`${nutrition.sodium}mg`} />
            <NRow label="Total Carbohydrate" value={`${nutrition.carbs}g`} />
            <NRow indent label="Sugars" value={`${nutrition.sugar}g`} />
            <NRow label="Protein" value={`${nutrition.protein}g`} />
          </div>
        </div>
      )}
      <div style={{ fontSize: ptPx(fsIngredients), lineHeight: 1.25 }} className="mb-1" data-testid="label-ingredients"><b>INGREDIENTS:</b> {ingredients}</div>
      {allergens.length > 0 && (
        <div className="font-bold uppercase p-1 border-2 border-[#3B2C24] mb-1" style={{ fontSize: ptPx(Math.max(6, fsIngredients - 1)) }} data-testid="label-allergens">
          CONTAINS: {allergens.join(", ")}
        </div>
      )}
      {bestBy && <div className="italic text-[#857163]" style={{ fontSize: ptPx(Math.max(6, fsIngredients - 1)) }} data-testid="label-best-by">{bestBy}</div>}
    </div>
  );
}


function NRow({ label, value, indent }) {
  return <div className={`flex justify-between ${indent ? "pl-3" : ""}`}><span>{label}</span><span className="font-semibold">{value}</span></div>;
}
